import { isBefore, isEqual } from "date-fns";
import { useCallback, useMemo, useState } from "react";

import { DateChangeProps } from "shared/types";
import { getRegionalSettingsOptions, isoToUTCString } from "shared/utils";

interface Props {
  includeEqual?: boolean;
  defaultValues?: {
    startDate?: string;
    startTime?: string;
    startTimezone?: string;
    endDate?: string;
    endTime?: string;
    endTimezone?: string;
  };
  onStartDateChange?: (time?: string) => void;
  onEndDateChange?: (time?: string) => void;
  includeTime?: boolean;
}
export const useIsStartDateBeforeEndDate = ({
  includeEqual = false,
  defaultValues,
  onStartDateChange,
  onEndDateChange,
  includeTime = true,
}: Props) => {
  const [start, setStart] = useState<DateChangeProps | null>({
    date: defaultValues?.startDate,
    time: defaultValues?.startTime,
    timeZone: defaultValues?.startTimezone,
  });
  const [end, setEnd] = useState<DateChangeProps | null>({
    date: defaultValues?.endDate,
    time: defaultValues?.endTime,
    timeZone: defaultValues?.endTimezone,
  });

  const handleStartDateChange = useCallback(
    ({ date, time, timeZone }: DateChangeProps) => {
      const dateTime = {
        date: date || start?.date,
        time: time || start?.time,
        timeZone: timeZone || start?.timeZone,
      };

      onStartDateChange && onStartDateChange(time);
      setStart(dateTime);
    },
    [start?.date, start?.time, start?.timeZone, onStartDateChange]
  );

  const handleEndDateChange = useCallback(
    ({ date, time, timeZone }: DateChangeProps) => {
      const dateTime = {
        date: date || end?.date,
        time: time || end?.time,
        timeZone: timeZone || end?.timeZone,
      };

      onEndDateChange && onEndDateChange(time);
      setEnd(dateTime);
    },
    [end?.date, end?.time, end?.timeZone, onEndDateChange]
  );

  const isStartBeforeEnd = useMemo(() => {
    const { timezoneToUtc } = getRegionalSettingsOptions();

    if (!start?.date || !end?.date) return true;

    const startDate = new Date(
      isoToUTCString({
        iso: start?.date,
        ...(includeTime && { hour: start?.time }),
        timeZone: start?.timeZone ? timezoneToUtc(start?.timeZone) : undefined,
      })
    );

    const endDate = new Date(
      isoToUTCString({
        iso: end?.date,
        ...(includeTime && { hour: end?.time }),
        timeZone: end?.timeZone ? timezoneToUtc(end?.timeZone) : undefined,
      })
    );

    return isBefore(startDate, endDate) || (includeEqual && isEqual(startDate, endDate));
  }, [
    end?.date,
    end?.time,
    end?.timeZone,
    start?.date,
    start?.time,
    start?.timeZone,
    includeEqual,
    includeTime,
  ]);

  return {
    start,
    end,
    isStartBeforeEnd,
    handleStartDateChange,
    handleEndDateChange,
  };
};
